import { useCallback, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import {
  harmonicAssessmentRunCreateRequestSchema,
  type HarmonicAssessmentRunCreateRequest,
} from "../services/harmonics";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import { FaultRunSelector } from "./FaultRunSelector";
import { LoadRunSelector } from "./LoadRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { NOT_MAXIMUM_SSC_NOTE, PROFILE_NOTE as PF_PROFILE_NOTE, sscBusMismatchNote } from "./PfCorrectionForm";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import {
  DEMAND_SOURCE_CHOICE_LABELS,
  DEMAND_SOURCES,
  HARMONICS_DRAFT_MODULE,
  HARMONICS_LABELS,
  SHORT_CIRCUIT_SOURCE_CHOICE_LABELS,
  SHORT_CIRCUIT_SOURCES,
  SPECTRUM_MODE_LABELS,
  SPECTRUM_MODES,
  buildHarmonicsRunPayload,
  createInitialHarmonicsDraft,
  createLoadRowDraft,
  harmonicsDraftSchema,
  seedLoadRowIdCounter,
  type DemandSourceChoice,
  type HarmonicLoadDraft,
  type HarmonicsDraft,
  type ShortCircuitSourceChoice,
  type SpectrumMode,
} from "./harmonicsDraft";

export const SPECTRUM_HINT =
  "The harmonic currents the vendor's data or a measurement give for this load, as " +
  "order:value pairs separated by commas, e.g. 5:100, 7:70, 11:45, 13:35 - in amperes, or as " +
  "a percent of I1. The product ships no typical spectrum; name the source.";

export const DEMAND_HINT =
  "The maximum demand current IL at the point of common coupling (TDD and Isc / IL are " +
  "formed over it): the linked load run's demand kVA over √3 U, or a typed figure with its " +
  "source.";

export const LIMIT_HINT =
  "The limit the project's design basis or the utility's connection agreement declares, with " +
  "its source: compared and labelled \"declared, not verified\" until the register holds a " +
  "verified table. Leave blank to state the distortion without judging it.";

export const SHORT_CIRCUIT_HINT_11B =
  "The bus short-circuit power S_sc = U² / |Z_k| from the linked fault run; the harmonic " +
  "voltages take the source impedance per order as h × |Z_k| (conservative). Isc for Isc / " +
  "IL is the run's Ik''.";

export const PROFILE_NOTE = PF_PROFILE_NOTE;

type HarmonicFormProps = {
  disabled?: boolean;
  onSubmit: (payload: HarmonicAssessmentRunCreateRequest) => void | Promise<void>;
  sldElements?: SldElements | null | undefined;
};

// The harmonic assessment study (EOS-11b, A32). The form owns the draft; the loads are rows
// the engineer adds and removes; the demand current and the short-circuit power start on the
// linked runs of the active open revision. The draft is kept under its own module key,
// "harmonics". The payload carries no project revision: the page adds the selected revision.
export function HarmonicForm({ disabled = false, onSubmit, sldElements }: HarmonicFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<HarmonicsDraft>({
      module: HARMONICS_DRAFT_MODULE,
      createInitial: createInitialHarmonicsDraft,
      schema: harmonicsDraftSchema,
      revisionId,
    });
  // A restored draft's row ids must not collide with the ids of rows added now.
  seedLoadRowIdCounter(draft.loads.map((row) => row.id));

  const [validationError, setValidationError] = useState<string | null>(null);

  function updateStudy(patch: Partial<HarmonicsDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateRow(id: string, patch: Partial<HarmonicLoadDraft>) {
    setDraft((current) => ({
      ...current,
      loads: current.loads.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  }

  function addRow() {
    setDraft((current) => ({ ...current, loads: [...current.loads, createLoadRowDraft()] }));
  }

  // The last row stays: the backend needs at least one load.
  function removeRow(id: string) {
    setDraft((current) =>
      current.loads.length <= 1
        ? current
        : { ...current, loads: current.loads.filter((row) => row.id !== id) },
    );
  }

  function chooseBus(busCode: string) {
    const bus = sldElementsOf(sldElements, ["bus"]).find((element) => element.code === busCode);
    setDraft((current) => ({
      ...current,
      busCode,
      systemNominalVoltageV:
        current.systemNominalVoltageV.trim() === "" && bus?.voltage_v
          ? bus.voltage_v
          : current.systemNominalVoltageV,
    }));
  }

  const setLoadRunId = useCallback(
    (loadRunId: string) => setDraft((current) => ({ ...current, loadRunId })),
    [setDraft],
  );
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  function clearForm() {
    setValidationError(null);
    clear();
  }

  function describeIssue(issue: ValidationIssueLike): string {
    const last = issue.path.at(-1);
    if (issue.code === "custom" && (last === "load_run_id" || last === "fault_run_id")) {
      return issue.message;
    }
    return describeValidationIssue(issue, HARMONICS_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = harmonicAssessmentRunCreateRequestSchema.safeParse(buildHarmonicsRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the harmonic assessment inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (
    label: string,
    field: keyof HarmonicsDraft,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={field}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<HarmonicsDraft>)}
      />
    </label>
  );

  const decimal = (label: string, field: keyof HarmonicsDraft) => studyText(label, field, "decimal");

  const rowText = (
    row: HarmonicLoadDraft,
    label: string,
    field: keyof HarmonicLoadDraft,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${field}-${row.id}`}
        value={row[field]}
        onChange={(event) => updateRow(row.id, { [field]: event.target.value } as Partial<HarmonicLoadDraft>)}
      />
    </label>
  );

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Harmonic assessment inputs" onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice onKeep={keepForThisRevision} onClear={clearForm} disabled={disabled} />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code")}
        {studyText("Study name", "name")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={chooseBus}
          categories={["bus"]}
          elements={sldElements}
        />
        {decimal("System nominal voltage (V)", "systemNominalVoltageV")}
        {decimal("Frequency (Hz)", "frequencyHz")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">
              Australia / New Zealand (AS/NZS 3000) - reference data pending
            </option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Distorting loads</legend>
        <p data-spectrum-hint="true">{SPECTRUM_HINT}</p>
        {draft.loads.map((row, index) => (
          <fieldset key={row.id} data-load-row={row.id}>
            <legend>Load {index + 1}</legend>
            {rowText(row, "Load code", "code")}
            {rowText(row, "Fundamental current I1 (A)", "fundamentalCurrentA", "decimal")}
            <label>
              Spectrum figures
              <select
                name={`spectrumMode-${row.id}`}
                value={row.spectrumMode}
                onChange={(event) => updateRow(row.id, { spectrumMode: event.target.value as SpectrumMode })}
              >
                {SPECTRUM_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {SPECTRUM_MODE_LABELS[mode]}
                  </option>
                ))}
              </select>
            </label>
            {rowText(row, "Harmonic spectrum (order:value)", "spectrum")}
            {rowText(row, "Spectrum source", "spectrumSource")}
            {rowText(row, "Spectrum source date", "spectrumSourceDate")}
            <button type="button" onClick={() => removeRow(row.id)} disabled={draft.loads.length <= 1}>
              Remove load {index + 1}
            </button>
          </fieldset>
        ))}
        <button type="button" onClick={addRow}>
          Add load
        </button>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Demand current</legend>
        <label>
          Demand current source
          <select
            name="demandSource"
            value={draft.demandSource}
            onChange={(event) => updateStudy({ demandSource: event.target.value as DemandSourceChoice })}
          >
            {DEMAND_SOURCES.map((source) => (
              <option key={source} value={source}>
                {DEMAND_SOURCE_CHOICE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.demandSource === "LINKED" ? (
          <LoadRunSelector revisionId={revisionId} loadRunId={draft.loadRunId} onChange={setLoadRunId} />
        ) : draft.demandSource === "TYPED" ? (
          <>
            {decimal("Maximum demand current IL (A)", "typedDemandCurrentA")}
            {studyText("Demand current source text", "typedDemandSource")}
          </>
        ) : null}
        <p data-demand-hint="true">{DEMAND_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Short-circuit power</legend>
        <label>
          Short-circuit power source
          <select
            name="shortCircuitSource"
            value={draft.shortCircuitSource}
            onChange={(event) =>
              updateStudy({ shortCircuitSource: event.target.value as ShortCircuitSourceChoice })
            }
          >
            {SHORT_CIRCUIT_SOURCES.map((source) => (
              <option key={source} value={source}>
                {SHORT_CIRCUIT_SOURCE_CHOICE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.shortCircuitSource === "LINKED" ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak={false}
            notMaximumNote={NOT_MAXIMUM_SSC_NOTE}
            busMismatchNoteFor={sscBusMismatchNote}
          />
        ) : draft.shortCircuitSource === "TYPED" ? (
          <>
            {decimal("Short-circuit power S_sc (kVA)", "typedShortCircuitPowerKva")}
            {studyText("Short-circuit power source text", "typedShortCircuitSource")}
          </>
        ) : null}
        <p data-short-circuit-hint="true">{SHORT_CIRCUIT_HINT_11B}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Declared harmonic limit</legend>
        {decimal("Declared THDv limit (%)", "thdvLimitPercent")}
        {decimal("Declared TDD limit (%)", "tddLimitPercent")}
        {studyText("Limit source", "limitSource")}
        <p data-limit-hint="true">{LIMIT_HINT}</p>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Assess the harmonics
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default HarmonicForm;
